import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type SubscriptionDetail } from '../api.js';
import { Topbar } from './AdminLayout.js';
import { fmtAmountPlain, fmtDate } from '../utils/format.js';
import { isolate } from '../utils/bidi.js';

export function SubscriptionDetailPage(): JSX.Element {
  const { id } = useParams();
  const nav = useNavigate();
  const [sub, setSub] = useState<SubscriptionDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [autoSyncing, setAutoSyncing] = useState(false);

  const reload = (): void => {
    api
      .getSubscription(id!)
      .then(setSub)
      .catch((e: Error) => setErr(e.message));
  };

  useEffect(() => {
    if (!id) return;
    reload();
    // Авто-синк статуса с AllPay при открытии карточки — 1 запрос на 1 подписку.
    // Тихо: если AllPay недоступен, страница всё равно показывает текущие данные.
    setAutoSyncing(true);
    api
      .syncSubscriptionStatus(id)
      .then(() => reload())
      .catch(() => undefined)
      .finally(() => setAutoSyncing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onCancel = async (): Promise<void> => {
    if (!sub) return;
    const subjName = sub.plan?.name ?? sub.product?.name ?? '';
    if (!confirm(`Отменить подписку ${sub.customerEmail} на «${isolate(subjName)}»?`)) return;
    setBusy(true);
    // Сбрасываем прошлую ошибку: иначе после удачной повторной попытки над уже
    // отменённой подпиской так и висела бы плашка «Подписка не отменена».
    setErr(null);
    try {
      await api.cancelSubscription(sub.id);
      reload();
    } catch (e) {
      const code = ((e as { details?: { error?: string } }).details ?? {}).error;
      const human: Record<string, string> = {
        provider_error: 'Не удалось связаться с AllPay. Подписка не отменена — попробуйте ещё раз.',
        provider_rejected: 'AllPay отказал в отмене. Подписка не отменена — проверьте её в кабинете AllPay.',
        cancel_not_confirmed:
          'AllPay не подтвердил отмену. Статус не изменён — проверьте подписку в кабинете AllPay; фоновая сверка подтянет итог сама.',
      };
      setErr((code && human[code]) || (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!sub) {
    return (
      <>
        <Topbar title="Подписка" />
        <div style={{ padding: 32, color: 'var(--fg-subtle)' }}>{err ?? 'Загрузка…'}</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={<>Подписка: <bdi>{sub.customerName || sub.customerEmail}</bdi></>}
        actions={
          <>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => nav('/admin/subscriptions')}
            >
              Назад
            </button>
            {sub.status === 'active' && (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => void onCancel()}
                disabled={busy}
              >
                Отменить
              </button>
            )}
          </>
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        {err && (
          <div
            className="card"
            style={{
              padding: 14,
              marginBottom: 14,
              color: 'var(--danger-fg)',
              background: 'var(--danger-subtle-bg)',
            }}
          >
            {err}
          </div>
        )}
        <div style={{ display: 'grid', gap: 14, maxWidth: 720 }}>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Сводка</div>
            </div>
            <div className="card-body stack">
              <Row
                label="Клиент"
                value={
                  <>
                    <bdi>{sub.customerName}</bdi> &lt;{sub.customerEmail}&gt;
                  </>
                }
              />
              <Row label="Телефон" value={sub.customerPhone} />
              <Row
                label="Продукт"
                value={(() => {
                  const name = sub.plan?.name ?? sub.product?.name ?? '—';
                  const slug = sub.plan?.slug ?? sub.product?.slug ?? '';
                  return (
                    <>
                      <bdi>{name}</bdi>
                      {slug ? ` (${slug})` : ''}
                    </>
                  );
                })()}
              />
              <Row
                label="Сумма"
                value={`₪ ${fmtAmountPlain(
                  sub.plan?.priceIls ?? sub.product?.priceIls ?? sub.payment?.amount ?? 0,
                )} / мес`}
              />
              <Row
                label="Статус"
                value={autoSyncing ? `${sub.status} · синхронизируем…` : sub.status}
              />
              <Row label="Создана" value={fmtDate(sub.createdAt)} />
              <Row
                label="Старт списаний"
                value={sub.startedAt ? fmtDate(sub.startedAt) : '—'}
              />
              {sub.cancelledAt && (
                <>
                  <Row label="Отменена" value={fmtDate(sub.cancelledAt)} />
                  <Row label="Кем отменена" value={sub.cancelledBy ?? '—'} />
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">История списаний ({sub.payments.length})</div>
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Дата</th>
                  <th style={{ textAlign: 'right' }}>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {sub.payments.map((p) => (
                  <tr key={p.id} className="no-hover">
                    <td>{p.paymentNumber}</td>
                    <td>
                      <span className="subtle" style={{ fontSize: 12.5 }}>
                        {fmtDate(p.createdAt)}
                      </span>
                    </td>
                    <td className="num" style={{ textAlign: 'right' }}>
                      {p.currency} {fmtAmountPlain(p.amount)}
                    </td>
                  </tr>
                ))}
                {sub.payments.length === 0 && (
                  <tr className="no-hover">
                    <td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--fg-subtle)' }}>
                      Списаний ещё не было
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 13,
      }}
    >
      <span style={{ color: 'var(--fg-subtle)' }}>{label}</span>
      <span style={{ fontFamily: 'inherit', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
